import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const AudiologySpeechPathologyCoursePage: React.FC = () => {
  const audiologyData = {
    title: "Bachelor of Science in Audiology & Speech Language Pathology",
    shortName: "B.Sc ASLP",
    duration: "4 Years",
    description: "A comprehensive program focusing on the diagnosis and treatment of hearing, speech, language, and communication disorders across all age groups.",
    overview: "B.Sc Audiology & Speech Language Pathology prepares students to become qualified audiologists and speech-language pathologists. The program covers hearing science, speech and language development, communication disorders, diagnostic procedures, and therapeutic interventions. Students learn to assess and treat various communication disorders, fit hearing aids, and provide rehabilitation services for individuals with hearing and speech challenges.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Normal hearing and speech abilities",
      "Good communication skills",
      "Empathy and patience with patients"
    ],
    careerProspects: {
      roles: [
        "Audiologist",
        "Speech Therapist",
        "Speech-Language Pathologist",
        "Hearing Aid Specialist",
        "Communication Disorder Specialist",
        "Pediatric Speech Therapist",
        "Adult Rehabilitation Specialist",
        "Educational Audiologist",
        "Clinical Researcher",
        "Rehabilitation Coordinator"
      ],
      industries: [
        "Hospitals and Clinics",
        "Speech and Hearing Centers",
        "Rehabilitation Centers",
        "Special Education Schools",
        "Hearing Aid Companies",
        "Research Institutions",
        "Government Health Services",
        "Private Practice",
        "Corporate Wellness Programs",
        "Geriatric Care Centers"
      ],
      averageSalary: "₹3-7 LPA (varies by experience and specialization)",
      topRecruiters: [
        "AIIMS Speech & Hearing",
        "Apollo Speech Therapy",
        "Fortis Healthcare",
        "Max Healthcare",
        "Manipal Hospitals",
        "Hearing Aid Companies",
        "Special Schools",
        "Government Hospitals",
        "Rehabilitation Centers",
        "Research Institutes"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy & Physiology",
          "Psychology",
          "Linguistics",
          "Physics of Sound",
          "Normal Speech & Language Development",
          "Introduction to Communication Disorders",
          "Research Methods",
          "Professional Ethics"
        ]
      },
      {
        year: 2,
        subjects: [
          "Hearing Science",
          "Speech Science",
          "Audiological Assessment",
          "Language Disorders",
          "Speech Disorders",
          "Neuroanatomy",
          "Statistics",
          "Clinical Observation"
        ]
      },
      {
        year: 3,
        subjects: [
          "Hearing Aids & Assistive Technology",
          "Pediatric Audiology",
          "Adult Speech Pathology",
          "Voice Disorders",
          "Fluency Disorders",
          "Swallowing Disorders",
          "Clinical Practice",
          "Community Audiology"
        ]
      },
      {
        year: 4,
        subjects: [
          "Advanced Clinical Practice",
          "Cochlear Implants",
          "Counseling in Communication Disorders",
          "Geriatric Audiology",
          "Rehabilitation Technology",
          "Research Project",
          "Professional Development",
          "Comprehensive Examination"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc ASLP",
      "Entrance examination (may include hearing/speech tests)",
      "Merit-based selection and counseling",
      "Hearing and speech screening",
      "Document verification",
      "Fee payment and admission",
      "Orientation program",
      "Begin academic and clinical training"
    ],
    topColleges: [
      {
        name: "All India Institute of Speech and Hearing",
        location: "Mysore, Karnataka",
        fees: "₹50,000 PA",
        ranking: "Premier Government Institute"
      },
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu",
        fees: "₹2.5 LPA",
        ranking: "Top Private Medical College"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka",
        fees: "₹4 LPA",
        ranking: "Deemed University"
      },
      {
        name: "JSS Institute of Speech and Hearing",
        location: "Mysore, Karnataka",
        fees: "₹1.8 LPA",
        ranking: "Specialized Institute"
      },
      {
        name: "Government Institute of Speech and Hearing",
        location: "Secunderabad, Telangana",
        fees: "₹25,000 PA",
        ranking: "Government Institute"
      }
    ],
    skills: [
      "Diagnostic Assessment",
      "Therapeutic Techniques",
      "Patient Counseling",
      "Hearing Aid Fitting",
      "Communication Skills",
      "Empathy and Patience",
      "Technical Skills",
      "Documentation",
      "Research Abilities",
      "Problem Solving",
      "Team Collaboration",
      "Continuous Learning"
    ],
    futureScope: "The field is expanding with technological advances like cochlear implants, digital hearing aids, and telepractice services. Future opportunities include specialization in areas like tinnitus management, vestibular rehabilitation, and pediatric cochlear implant programming. The integration of AI and mobile health technologies is creating new possibilities for remote therapy delivery and automated screening programs.",
    faqs: [
      {
        question: "What is the difference between an Audiologist and Speech Therapist?",
        answer: "Audiologists specialize in hearing assessment, hearing aid fitting, and hearing rehabilitation. Speech therapists focus on speech, language, voice, and swallowing disorders. Many programs train professionals in both areas."
      },
      {
        question: "Can I specialize in pediatric or adult populations?",
        answer: "Yes, you can specialize in pediatric audiology and speech therapy, adult rehabilitation, geriatric services, or specific disorders like autism spectrum disorders or voice disorders."
      },
      {
        question: "Is there scope for private practice?",
        answer: "Yes, many audiologists and speech therapists run successful private clinics, especially in urban areas where there's growing awareness of communication disorders and hearing health."
      },
      {
        question: "What are the research opportunities?",
        answer: "Research opportunities exist in areas like hearing conservation, speech technology development, communication aid design, and effectiveness studies of therapeutic interventions."
      },
      {
        question: "How important is the clinical training component?",
        answer: "Clinical training is crucial, typically comprising 25-30% of the program. Students gain hands-on experience in assessment, diagnosis, and treatment under supervision in various clinical settings."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {audiologyData.title} ({audiologyData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={audiologyData} />
      </div>
    </div>
  );
};

export default AudiologySpeechPathologyCoursePage;